import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPlayer, renderStrokes } from '../drawingReplay';
import { recordChainGif } from '../chainGifExport';
import { audio } from '../audio';
import PlayerAvatar from './PlayerAvatar';
import './Results.css';

const PLAYBACK_SPEED = 3;
const TYPING_DELAY = 1000;       // ms to show "..." typing indicator
const TEXT_LINGER_DELAY = 2500;   // ms to linger on text before advancing
const DRAWING_DONE_DELAY = 1500;  // ms after drawing finishes before advancing

const REACTION_EMOJIS = ['😂', '🔥', '💀', '❤️', '👍', '👏', '😮', '🎨'];
const FLOAT_DURATION_MS = 2500;

/* ── Typing indicator (bouncing dots) ── */
function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <span className="typing-dot" />
      <span className="typing-dot" />
      <span className="typing-dot" />
    </div>
  );
}

/* ── Animated drawing playback inside a bubble ── */
function DrawingPlayback({ drawingData, isActive, onFinished }) {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const onFinishedRef = useRef(onFinished);
  const hasFinishedRef = useRef(false);

  onFinishedRef.current = onFinished;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive) return;
    hasFinishedRef.current = false;

    const finish = () => {
      if (hasFinishedRef.current) return;
      hasFinishedRef.current = true;
      onFinishedRef.current?.();
    };

    const strokes = drawingData?.strokes || [];

    if (strokes.length === 0) {
      const t = setTimeout(finish, 800);
      return () => clearTimeout(t);
    }

    const player = createPlayer(canvas, drawingData, {
      speed: PLAYBACK_SPEED,

      onFinished: finish,
    });
    playerRef.current = player;
    player.play();

    // Safety timeout: if playback hasn't finished in 30s, force-finish
    const safetyTimer = setTimeout(() => {
      console.warn('[DrawingPlayback] Safety timeout — forcing finish');
      finish();
    }, 30000);

    return () => {
      clearTimeout(safetyTimer);
      player.destroy();
      playerRef.current = null;
    };
  }, [isActive, drawingData]);

  return <canvas ref={canvasRef} className="msg-drawing-canvas" />;
}

/* ── Static drawing (for completed messages) ── */
function StaticDrawing({ drawingData }) {
  const canvasRef = useRef(null);
  const isEmpty = !drawingData || !drawingData.strokes || drawingData.strokes.length === 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !drawingData) return;
    const config = drawingData.canvas || { width: 800, height: 600, background: '#ffffff' };
    canvas.width = config.width || 800;
    canvas.height = config.height || 600;
    const ctx = canvas.getContext('2d');
    renderStrokes(ctx, drawingData.strokes || [], Infinity, config.background || '#ffffff');
  }, [drawingData]);

  if (isEmpty) {
    return (
      <div className="msg-drawing-empty">
        <span className="material-symbols-outlined" style={{ fontSize: 36, color: 'var(--color-text-light)', opacity: 0.5 }}>
          person_off
        </span>
        <span style={{ fontSize: 13, color: 'var(--color-text-light)', fontWeight: 600 }}>
          Player didn't draw
        </span>
      </div>
    );
  }

  return <canvas ref={canvasRef} className="msg-drawing-canvas" />;
}

/* ── Floating reaction emoji ── */
function FloatingReaction({ emoji, x, playerName }) {
  return (
    <div className="floating-reaction" style={{ left: `${x}%` }}>
      <span className="floating-reaction-emoji">{emoji}</span>
      {playerName && <span className="floating-reaction-name">{playerName}</span>}
    </div>
  );
}

/* ── A single scrapbook step card ── */
function ChatMessage({ step, state, stepIndex, chainIndex, onPlaybackFinished, onReport }) {
  // state: 'typing' | 'revealing' | 'done'
  const isText = step.type === 'prompt' || step.type === 'guess';
  const label = step.type === 'prompt' ? 'wrote' : step.type === 'drawing' ? 'drew' : 'guessed';
  const showTape = stepIndex % 2 === 0;
  const isTyping = state === 'typing';

  // Reporting: a tiny, unobtrusive flag in the card corner. 'idle' shows the
  // flag, 'confirm' a small inline yes/no, 'reported' a quiet acknowledgement.
  const [reportState, setReportState] = useState('idle');
  const canReport = onReport && step.id && !isTyping && reportState !== 'reported';

  return (
    <div className={`msg-row msg-row-${stepIndex % 2 === 0 ? 'left' : 'right'} fade-in`}>
      <div className={`msg-card msg-card-${step.type}`} data-step={stepIndex}>
        {showTape && <span className="msg-card-tape" aria-hidden="true" />}

        {canReport && (
          <div className="msg-report">
            {reportState === 'idle' ? (
              <button
                type="button"
                className="msg-report-btn"
                title="Report this"
                aria-label="Report this content"
                onClick={() => setReportState('confirm')}
              >
                <span className="material-symbols-outlined">flag</span>
              </button>
            ) : (
              <div className="msg-report-confirm" role="group" aria-label="Confirm report">
                <span className="msg-report-q">Report?</span>
                <button
                  type="button"
                  className="msg-report-yes"
                  onClick={() => { onReport(step.id); setReportState('reported'); }}
                >
                  Report
                </button>
                <button
                  type="button"
                  className="msg-report-no"
                  aria-label="Cancel"
                  onClick={() => setReportState('idle')}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            )}
          </div>
        )}
        {reportState === 'reported' && (
          <div className="msg-report-done" role="status">
            <span className="material-symbols-outlined">check</span> Reported
          </div>
        )}

        <div className="msg-card-header">
          <div className="msg-card-avatar">
            <PlayerAvatar avatar={step.player_avatar} fallback={step.player_name?.charAt(0).toUpperCase()} size={28} />
          </div>
          <span className="msg-card-meta">
            <span className="msg-card-name">{step.player_name}</span>
            <span className="msg-card-action"> {label}</span>
          </span>
        </div>

        <div className={`msg-card-body ${isTyping ? 'is-typing' : ''}`}>
          {isText && (
            <div className="msg-text-wrap">
              {/* Always rendered so the card sizes to the final text. Hidden via
                  CSS during typing and animated in via .pop-in on reveal. */}
              <span className={`msg-text ${state === 'revealing' ? 'pop-in' : ''}`}>
                {step.text}
              </span>
            </div>
          )}

          {!isText && (
            <div className="msg-drawing">
              <div className="msg-drawing-wrapper">
                {state === 'revealing' && (
                  <DrawingPlayback
                    key={`${chainIndex}-${stepIndex}`}
                    drawingData={step.drawing_data}
                    isActive={true}
                    onFinished={onPlaybackFinished}
                  />
                )}
                {state === 'done' && (
                  <StaticDrawing drawingData={step.drawing_data} />
                )}
                {/* During typing: wrapper is empty — its fixed dimensions reserve
                    the final layout so the card doesn't grow when content arrives. */}
              </div>
            </div>
          )}

          {isTyping && (
            <div className="msg-card-typing-overlay" aria-hidden="true">
              <TypingIndicator />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   Main Results component
   ════════════════════════════════════════════ */

function Results({ chains, isHost = true, playerId, playerName, onAdvanceChain, forcedChainIndex, onStepAdvanced, forcedStep, onEndGame, onPlayAgain, onReportContent, onLeaveGame, initialChainIndex, initialStepIndex, onSendReaction, reactionSignal }) {
  const [currentChainIndex, setCurrentChainIndex] = useState(initialChainIndex ?? 0);
  const [gifProgress, setGifProgress] = useState(null);
  // visibleCount = how many steps have begun appearing (typing or done)
  const [visibleCount, setVisibleCount] = useState(0);
  // revealedSet tracks which step indices have moved past "typing" → content shown
  const [revealedSet, setRevealedSet] = useState(new Set());
  // doneSet tracks steps fully completed (content lingered, ready to scroll past)
  const [doneSet, setDoneSet] = useState(new Set());
  const [chainComplete, setChainComplete] = useState(false);
  // Highest chain index that has been fully played through — allows re-navigation
  const [highWaterMark, setHighWaterMark] = useState(initialChainIndex ?? 0);

  // [{ id, emoji, x, playerName }] — active floating reactions
  const [floatingReactions, setFloatingReactions] = useState([]);

  // Confirmation before leaving — bailing mid-reveal is easy to do by accident
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Tracks previous chain index so we can distinguish initial mount from chain change
  const prevChainIndexRef = useRef(null);

  const scrollRef = useRef(null);
  // Stable refs so timer callbacks and markStepDone don't go stale
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;
  const onStepAdvancedRef = useRef(onStepAdvanced);
  onStepAdvancedRef.current = onStepAdvanced;
  const currentChainIndexRef = useRef(currentChainIndex);
  currentChainIndexRef.current = currentChainIndex;

  const currentChain = chains[currentChainIndex];
  const steps = currentChain?.steps || [];

  /* ── React to host-driven chain advance ── */
  useEffect(() => {
    if (isHost || forcedChainIndex === undefined || forcedChainIndex < 0) return;
    if (forcedChainIndex < chains.length) {
      setCurrentChainIndex(forcedChainIndex);
    }
  }, [forcedChainIndex, isHost, chains.length]);

  /* ── React to host-driven step advance (non-host sync) ── */
  useEffect(() => {
    if (isHost || !forcedStep) return;
    if (forcedStep.chainIndex !== currentChainIndex) return;
    const idx = forcedStep.stepIndex;
    // markStepDone is idempotent (guards against already-done steps)
    markStepDone(idx);
  }, [forcedStep]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Reset when chain changes; fast-forward on first mount for rejoin ── */
  useEffect(() => {
    // Strict mode runs effects twice. The second run sees prevChainIndexRef already
    // set to currentChainIndex from the first run, so we skip it safely.
    if (prevChainIndexRef.current === currentChainIndex) return;

    if (prevChainIndexRef.current === null) {
      // Initial mount — fast-forward non-host to the current position
      prevChainIndexRef.current = currentChainIndex;
      if (initialStepIndex != null && initialStepIndex >= 0) {
        const n = initialStepIndex + 1; // steps already done
        const stepsList = chains[currentChainIndex]?.steps || [];
        const allDone = n >= stepsList.length;
        setDoneSet(new Set(Array.from({ length: n }, (_, i) => i)));
        setRevealedSet(new Set(Array.from({ length: n }, (_, i) => i)));
        if (allDone) {
          setVisibleCount(n);
          setChainComplete(true);
        } else {
          // Show the currently-playing step as 'typing' so it isn't hidden
          setVisibleCount(n + 1);
        }
      }
      return;
    }

    // Chain changed — full reset
    prevChainIndexRef.current = currentChainIndex;
    setVisibleCount(0);
    setRevealedSet(new Set());
    setDoneSet(new Set());
    setChainComplete(false);
  }, [currentChainIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Advance high-water mark when a chain finishes ── */
  useEffect(() => {
    if (chainComplete) {
      setHighWaterMark(prev => Math.max(prev, currentChainIndex));
    }
  }, [chainComplete, currentChainIndex]);

  /* ── Audio: fanfare on first mount ── */
  useEffect(() => {
    const t = setTimeout(() => audio.resultsFanfare(), 600);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Audio: bubble pop when a step moves from typing → revealed ── */
  const revealedSizeRef = useRef(0);
  useEffect(() => {
    const newSize = revealedSet.size;
    if (newSize > revealedSizeRef.current) {
      audio.bubblePop();
    }
    revealedSizeRef.current = newSize;
  }, [revealedSet]);

  /* ── Audio: soft ticking while typing indicator is shown ── */
  const hasTypingStep = visibleCount > 0 && !revealedSet.has(visibleCount - 1) && !chainComplete;
  useEffect(() => {
    if (!hasTypingStep) return;
    const interval = setInterval(() => audio.typingTick(), 320);
    return () => clearInterval(interval);
  }, [hasTypingStep]);

  /* ── Audio: chain complete sound ── */
  const chainCompletePlayedRef = useRef(false);
  useEffect(() => {
    if (chainComplete && !chainCompletePlayedRef.current) {
      chainCompletePlayedRef.current = true;
      audio.chainComplete();
    }
    if (!chainComplete) chainCompletePlayedRef.current = false;
  }, [chainComplete]);

  /* ── Kick off the first step when chain resets ── */
  useEffect(() => {
    if (visibleCount === 0 && steps.length > 0 && !chainComplete) {
      const t = setTimeout(() => setVisibleCount(1), 400);
      return () => clearTimeout(t);
    }
  }, [visibleCount, steps.length, chainComplete]);

  /* ── When a new step becomes visible, start its typing timer ── */
  useEffect(() => {
    if (visibleCount === 0 || chainComplete) return;
    const idx = visibleCount - 1;
    if (revealedSet.has(idx)) return; // already revealed

    const t = setTimeout(() => {
      setRevealedSet(prev => new Set(prev).add(idx));
    }, TYPING_DELAY);
    return () => clearTimeout(t);
  }, [visibleCount, revealedSet, chainComplete]);

  /* ── When a text step is revealed, linger then mark done + advance (host only) ── */
  useEffect(() => {
    if (!isHostRef.current) return; // non-hosts wait for server events
    if (chainComplete) return;
    // Find steps that are revealed but not yet done
    for (const idx of revealedSet) {
      if (doneSet.has(idx)) continue;
      const step = steps[idx];
      if (!step) continue;
      if (step.type === 'prompt' || step.type === 'guess') {
        const t = setTimeout(() => {
          markStepDone(idx);
        }, TEXT_LINGER_DELAY);
        return () => clearTimeout(t);
      }
      // For drawing steps, we wait for onPlaybackFinished callback
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealedSet, doneSet, chainComplete]);

  const markStepDone = useCallback((idx) => {
    setDoneSet(prev => {
      if (prev.has(idx)) return prev; // already done, no-op
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    // Host broadcasts the step advance so non-hosts stay in sync
    if (isHostRef.current && onStepAdvancedRef.current) {
      onStepAdvancedRef.current(currentChainIndexRef.current, idx);
    }
    // Advance to next step or complete chain
    if (idx < steps.length - 1) {
      setVisibleCount(idx + 2);
    } else {
      setChainComplete(true);
    }
  }, [steps.length]);

  const handlePlaybackFinished = useCallback((idx) => {
    if (!isHostRef.current) return; // non-hosts wait for server events
    setTimeout(() => {
      markStepDone(idx);
    }, DRAWING_DONE_DELAY);
  }, [markStepDone]);

  /* ── Helper: spawn a floating reaction ── */
  const spawnFloat = useCallback((emoji, name) => {
    const id = Date.now() + Math.random();
    const x = 10 + Math.random() * 70; // 10–80% within the reaction strip
    setFloatingReactions(prev => [...prev, { id, emoji, x, playerName: name }]);
    setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => r.id !== id));
    }, FLOAT_DURATION_MS + 100);
  }, []);

  /* ── Handle reactions received from other players ── */
  useEffect(() => {
    if (!reactionSignal) return;
    const { emoji, player_name } = reactionSignal;
    spawnFloat(emoji, player_name);
  }, [reactionSignal, spawnFloat]);

  /* ── Handle local player tapping a reaction emoji (rate-limited to 1/sec) ── */
  const lastReactTimeRef = useRef(0);
  const handleReact = useCallback((emoji) => {
    const now = Date.now();
    if (now - lastReactTimeRef.current < 1000) return;
    lastReactTimeRef.current = now;
    spawnFloat(emoji, playerName);
    if (onSendReaction) onSendReaction(emoji);
  }, [playerName, onSendReaction, spawnFloat]);

  /* ── Auto-scroll to bottom ── */
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [visibleCount, revealedSet, doneSet, chainComplete]);

  const goToNextChain = () => {
    const newIndex = currentChainIndex + 1;
    if (isHost && onAdvanceChain) onAdvanceChain(newIndex);
    setCurrentChainIndex(newIndex);
  };

  const handleSaveGif = useCallback(async () => {
    if (gifProgress !== null) return;
    setGifProgress(0);
    try {
      const result = await recordChainGif(currentChain, pct => {
        setGifProgress(Math.round(pct * 100));
      });
      if (!result) return;

      const { blob } = result;
      const slug = (currentChain.original_author || 'chain')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `pictaphone-${slug}.gif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Results] Save GIF failed:', err);
      setGifProgress(-1);
      setTimeout(() => setGifProgress(null), 2500);
      return;
    }
    setGifProgress(null);
  }, [currentChain, gifProgress]);

  /* ── Empty state ── */
  if (!chains || chains.length === 0) {
    return (
      <div className="results-container">
        <div className="results-card">
          <div className="results-intro">
            <div className="results-intro-eyebrow">all done!</div>
            <h2 className="results-intro-title">
              <span className="results-intro-chain">no chains</span>
            </h2>
          </div>
          <p style={{ padding: '0 22px 22px', fontFamily: 'var(--font-hand)', fontSize: 18, color: 'var(--color-ink-soft)' }}>No results to show!</p>
        </div>
      </div>
    );
  }

  /* ── Derive per-step state ── */
  const getStepState = (idx) => {
    if (doneSet.has(idx)) return 'done';
    if (revealedSet.has(idx)) return 'revealing';
    if (idx < visibleCount) return 'typing';
    return 'hidden';
  };

  return (
    <div className="results-container">
      <div className="results-card">
        {/* Floating reactions overlay */}
        <div className="reactions-float-layer" aria-hidden="true">
          {floatingReactions.map(r => (
            <FloatingReaction key={r.id} emoji={r.emoji} x={r.x} playerName={r.playerName} />
          ))}
        </div>

        {/* Leave game — the immediate escape hatch from an uncomfortable reveal.
            Hidden while the confirm dialog is open so it isn't a second
            "Leave"-labeled button sitting dimmed behind the overlay. */}
        {onLeaveGame && !confirmLeave && (
          <button className="results-leave-btn" onClick={() => setConfirmLeave(true)} title="Leave game">
            <span className="material-symbols-outlined">arrow_back</span>
            Leave
          </button>
        )}

        {/* Confirm leaving — guards against an accidental tap on the escape hatch */}
        {onLeaveGame && confirmLeave && (
          <div
            className="leave-confirm-overlay"
            onClick={() => setConfirmLeave(false)}
            role="presentation"
          >
            <div
              className="leave-confirm-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="leave-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="leave-confirm-title" id="leave-confirm-title">Leave the game?</h3>
              <p className="leave-confirm-body">You'll miss the rest of the reveal. You can't rejoin once the results are over.</p>
              <div className="leave-confirm-actions">
                <button
                  className="leave-confirm-cancel"
                  onClick={() => setConfirmLeave(false)}
                  autoFocus
                >
                  Stay
                </button>
                <button
                  className="leave-confirm-go"
                  onClick={() => { setConfirmLeave(false); onLeaveGame(); }}
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Intro: scrapbook-style chain title */}
        <div className="results-intro">
          <div className="results-intro-eyebrow">
            chain {currentChainIndex + 1} of {chains.length}
          </div>
          <h2 className="results-intro-title">
            <span className="results-intro-avatar">
              <PlayerAvatar avatar={currentChain.original_author_avatar} fallback={currentChain.original_author?.charAt(0).toUpperCase()} size={36} />
            </span>
            <span className="results-intro-name">{currentChain.original_author}'s</span>
            <span className="results-intro-chain">chain</span>
          </h2>
        </div>

        <>

          {/* Message thread */}
          <div className="chat-thread" ref={scrollRef}>
            {steps.map((step, idx) => {
              const state = getStepState(idx);
              if (state === 'hidden') return null;
              return (
                <ChatMessage
                  key={`${currentChainIndex}-${idx}`}
                  step={step}
                  state={state}
                  chainIndex={currentChainIndex}
                  stepIndex={idx}
                  onPlaybackFinished={() => handlePlaybackFinished(idx)}
                  onReport={onReportContent}
                />
              );
            })}

            {/* Chain complete CTA */}
            {chainComplete && (
              <div className="chat-chain-complete fade-in">
                <div className="chat-complete-divider">
                  <span>Chain complete <span className="material-symbols-outlined" style={{fontSize: '16px', verticalAlign: 'middle'}}>celebration</span></span>
                </div>

                <button
                  className="save-image-button"
                  onClick={handleSaveGif}
                  disabled={gifProgress !== null && gifProgress !== -1}
                  title="Save as animated GIF"
                >
                  {gifProgress === null && <>↓ Save GIF</>}
                  {gifProgress !== null && gifProgress >= 0 && (
                    <><span className="save-image-spinner" />Encoding… {gifProgress}%</>
                  )}
                  {gifProgress === -1 && <>GIF failed</>}
                </button>

                {isHost ? (
                  currentChainIndex < chains.length - 1 ? (
                    <button className="next-chain-button" onClick={goToNextChain}>
                      Next Chain →
                    </button>
                  ) : (
                    <div className="results-final-actions">
                      {onPlayAgain && (
                        <button className="next-chain-button play-again-button" onClick={onPlayAgain}>
                          Play Again <span className="material-symbols-outlined" style={{fontSize: '16px', verticalAlign: 'middle'}}>replay</span>
                        </button>
                      )}
                      <button className="next-chain-button end-game-button" onClick={onEndGame}>
                        Finish Game <span className="material-symbols-outlined" style={{fontSize: '16px', verticalAlign: 'middle'}}>celebration</span>
                      </button>
                    </div>
                  )
                ) : (
                  <p className="waiting-for-host">Waiting for host…</p>
                )}
              </div>
            )}
          </div>
        </>

        {/* Global reaction emoji tray */}
        <div className="reaction-tray">
          {REACTION_EMOJIS.map(emoji => (
            <button
              key={emoji}
              className="reaction-tray-btn"
              onClick={() => handleReact(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Chain nav thumbnails */}
        <div className="results-thumbnails">
          {chains.map((chain, idx) => (
            <button
              key={idx}
              className={`thumbnail-btn ${idx === currentChainIndex ? 'active' : ''} ${idx <= highWaterMark && idx !== currentChainIndex ? 'viewed' : ''}`}
              onClick={() => {
                if (!isHost || idx > highWaterMark) return;
                if (onAdvanceChain) onAdvanceChain(idx);
                setCurrentChainIndex(idx);
              }}
              disabled={!isHost || idx > highWaterMark}
              style={!isHost ? { opacity: 0.4, cursor: 'default' } : undefined}
            >
              <div className="thumbnail-avatar">
                <PlayerAvatar avatar={chain.original_author_avatar} fallback={chain.original_author.charAt(0).toUpperCase()} size={28} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Results;
